/**
 * Permission check functions for the frontend.
 *
 * All permission logic lives here so components only need to import
 * from this single module. Super-admin checks are handled at the
 * AuthContext level — the cached permissions for super admins are
 * already set to all-true, so these functions work uniformly.
 */
import { AccountPermissions } from './api/client';

export type FeatureName =
  | 'chat_assistant'
  | 'assistant_setup'
  | 'assistant_content'
  | 'model_info'
  | 'notifications_settings'
  | 'mass_messages'
  | 'mass_messages_lists'
  | 'mass_messages_campaigns'
  | 'mass_messages_schedules'
  | 'priority_mass_messages'
  | 'priority_mass_messages_lists'
  | 'priority_mass_messages_campaigns'
  | 'priority_mass_messages_schedules'
  | 'automated_messages'
  | 'automated_messages_setup'
  | 'token'
  | 'pay'
  | 'censure'
  | 'stats'
  | 'reconnect'
  | 'audio_settings'
  | 'bulk_media'
  | 'dynamic_lists';

// ---------------------------------------------------------------------------
// Core checks
// ---------------------------------------------------------------------------

export function hasAccountAccess(
  permissions: AccountPermissions | undefined,
): boolean {
  if (!permissions) return false;
  return permissions.access_to_account;
}

export function isAccountOwner(
  permissions: AccountPermissions | undefined,
): boolean {
  if (!permissions) return false;
  return permissions.owner_access_to_account;
}

export function hasFeatureAccess(
  permissions: AccountPermissions | undefined,
  feature: FeatureName,
): boolean {
  if (!permissions) return false;
  if (permissions.owner_access_to_account) return true;
  const key = `access_${feature}` as keyof AccountPermissions;
  return permissions[key] === true;
}

// ---------------------------------------------------------------------------
// Per-feature convenience helpers
// ---------------------------------------------------------------------------

export function hasChatAssistantAccess(p: AccountPermissions | undefined): boolean {
  return hasFeatureAccess(p, 'chat_assistant');
}

export function hasAssistantSetupAccess(p: AccountPermissions | undefined): boolean {
  return hasFeatureAccess(p, 'assistant_setup');
}

export function hasAssistantContentAccess(p: AccountPermissions | undefined): boolean {
  return hasFeatureAccess(p, 'assistant_content');
}

export function hasModelInfoAccess(p: AccountPermissions | undefined): boolean {
  return hasFeatureAccess(p, 'model_info');
}

export function hasNotificationsSettingsAccess(p: AccountPermissions | undefined): boolean {
  return hasFeatureAccess(p, 'notifications_settings');
}

export function hasMassMessagesAccess(p: AccountPermissions | undefined): boolean {
  return hasFeatureAccess(p, 'mass_messages');
}

export function hasMassMessagesListsAccess(p: AccountPermissions | undefined): boolean {
  return hasFeatureAccess(p, 'mass_messages_lists');
}

export function hasMassMessagesCampaignsAccess(p: AccountPermissions | undefined): boolean {
  return hasFeatureAccess(p, 'mass_messages_campaigns');
}

export function hasMassCampaignManagementAccess(p: AccountPermissions | undefined): boolean {
  return isAccountOwner(p) || hasMassMessagesCampaignsAccess(p);
}

export function hasMassMessagesSchedulesAccess(p: AccountPermissions | undefined): boolean {
  return hasFeatureAccess(p, 'mass_messages_schedules');
}

export function hasPriorityMassMessagesAccess(p: AccountPermissions | undefined): boolean {
  if (!p) return false;
  if (p.owner_access_to_account) return true;
  return (
    p.access_priority_mass_messages === true ||
    p.access_priority_mass_messages_lists === true ||
    p.access_priority_mass_messages_campaigns === true ||
    p.access_priority_mass_messages_schedules === true
  );
}

export function hasPriorityMassMessagesListsAccess(p: AccountPermissions | undefined): boolean {
  return hasFeatureAccess(p, 'priority_mass_messages_lists');
}

export function hasPriorityMassMessagesCampaignsAccess(p: AccountPermissions | undefined): boolean {
  return hasFeatureAccess(p, 'priority_mass_messages_campaigns');
}

export function hasPriorityMassCampaignManagementAccess(p: AccountPermissions | undefined): boolean {
  return isAccountOwner(p) || hasPriorityMassMessagesCampaignsAccess(p);
}

export function hasPriorityMassMessagesSchedulesAccess(p: AccountPermissions | undefined): boolean {
  return hasFeatureAccess(p, 'priority_mass_messages_schedules');
}

export function hasAutomatedMessagesAccess(p: AccountPermissions | undefined): boolean {
  return hasFeatureAccess(p, 'automated_messages');
}

export function hasAutomatedMessagesSetupAccess(p: AccountPermissions | undefined): boolean {
  return hasFeatureAccess(p, 'automated_messages_setup');
}

export function hasTokenAccess(p: AccountPermissions | undefined): boolean {
  return hasFeatureAccess(p, 'token');
}

export function hasPayAccess(p: AccountPermissions | undefined): boolean {
  return hasFeatureAccess(p, 'pay');
}

/** Chat Assistant's per-Account Audio settings (AI PPV voice notes): Owner or
 *  audio_settings — not assistant_setup. The AI Audio Tool page has its own flag. */
export function hasAudioSettingsAccess(p: AccountPermissions | undefined): boolean {
  return hasFeatureAccess(p, 'audio_settings');
}

/** Bulk Media and the Vault Upload button on this Account (ADR 0031). */
export function hasBulkMediaAccess(p: AccountPermissions | undefined): boolean {
  return hasFeatureAccess(p, 'bulk_media');
}

export function hasCensureAccess(p: AccountPermissions | undefined): boolean {
  return hasFeatureAccess(p, 'censure');
}

export function hasStatsAccess(p: AccountPermissions | undefined): boolean {
  return hasFeatureAccess(p, 'stats');
}

export function hasAnyMassMessagesAccess(p: AccountPermissions | undefined): boolean {
  return hasMassMessagesListsAccess(p) || hasMassMessagesCampaignsAccess(p) || hasMassMessagesSchedulesAccess(p);
}

// ---------------------------------------------------------------------------
// Constant: all permissions granted (used for super-admin override)
// ---------------------------------------------------------------------------

export const FULL_PERMISSIONS: AccountPermissions = {
  owner_access_to_account: true,
  access_to_account: true,
  access_chat_assistant: true,
  access_assistant_setup: true,
  access_assistant_content: true,
  access_model_info: true,
  access_notifications_settings: true,
  access_mass_messages: true,
  access_mass_messages_lists: true,
  access_mass_messages_campaigns: true,
  access_mass_messages_schedules: true,
  access_priority_mass_messages: true,
  access_priority_mass_messages_lists: true,
  access_priority_mass_messages_campaigns: true,
  access_priority_mass_messages_schedules: true,
  access_automated_messages: true,
  access_automated_messages_setup: true,
  access_token: true,
  access_pay: true,
  access_censure: true,
  access_stats: true,
  access_reconnect: true,
  access_audio_settings: true,
  access_bulk_media: true,
  access_dynamic_lists: true,
  access_audio: true,
  access_tracking_links: true,
  access_permission_management: true,
};

export function hasReconnectAccess(p: AccountPermissions | undefined): boolean {
  return hasFeatureAccess(p, 'reconnect');
}
